import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTheme } from '../ThemeContext';
import styles from '../dashboard/Dashboard.module.css';
import Typography from '@mui/material/Typography';
import { renderToStaticMarkup } from 'react-dom/server';
import { GoogleMap, Marker, useJsApiLoader } from '@react-google-maps/api';
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, FormControl, IconButton, InputLabel, ListItemText, MenuItem, OutlinedInput, Select, TextField } from '@mui/material';
import { FiArrowDown, FiArrowUp, FiFileText, FiMapPin, FiTrash2 } from 'react-icons/fi';
import { MdLocalAirport } from 'react-icons/md';
import { useLocation } from 'react-router-dom';
import starred from '../icons/starred.png';
import staryellow from '../icons/staryellow.png';
import stargreen from '../icons/stargreen.png';

const logoContext = require.context('../icons/Logos', false, /\.(png|jpe?g|svg)$/i);

const containerStyle = {
  width: '100%',
  height: 'calc(100vh - 170px)'
};

const center = {
  lat: 18,
  lng: -155
};

const icons = {
  1: { url: starred },
  2: { url: staryellow },
  3: { url: stargreen }
};

const defaultMarkerIcon = {
  url: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="28" height="40" viewBox="0 0 28 40">
      <path fill="#6b7280" d="M14 0C6.268 0 0 6.268 0 14c0 10.5 14 26 14 26s14-15.5 14-26C28 6.268 21.732 0 14 0z"/>
      <circle cx="14" cy="14" r="6" fill="#f9fafb"/>
    </svg>
  `),
};

const createAirportMarkerIcon = (backgroundColor, iconColor, size = 34) => ({
  url: `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(`
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 34 34">
      <circle cx="17" cy="17" r="16" fill="${backgroundColor}" />
      <foreignObject x="5" y="5" width="24" height="24">
        <div xmlns="http://www.w3.org/1999/xhtml" style="display:flex;align-items:center;justify-content:center;width:24px;height:24px;color:${iconColor};">
          ${renderToStaticMarkup(<MdLocalAirport size={18} />)}
        </div>
      </foreignObject>
    </svg>
  `)}`,
});

const nearestAirportMarkerIcon = createAirportMarkerIcon('#f59e0b', '#111827', 38);

const supportStatusDescriptions = {
  1: 'Basic',
  2: 'Standard',
  3: 'Premium'
};

const darkStyle = [
  { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
  { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
  { featureType: 'administrative.locality', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'poi.park', elementType: 'geometry', stylers: [{ color: '#263c3f' }] },
  { featureType: 'poi.park', elementType: 'labels.text.fill', stylers: [{ color: '#6b9a76' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#38414e' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#212a37' }] },
  { featureType: 'road', elementType: 'labels.text.fill', stylers: [{ color: '#9ca5b3' }] },
  { featureType: 'road.highway', elementType: 'geometry', stylers: [{ color: '#746855' }] },
  { featureType: 'road.highway', elementType: 'geometry.stroke', stylers: [{ color: '#1f2835' }] },
  { featureType: 'road.highway', elementType: 'labels.text.fill', stylers: [{ color: '#f3d19c' }] },
  { featureType: 'transit', elementType: 'geometry', stylers: [{ color: '#2f3948' }] },
  { featureType: 'transit.station', elementType: 'labels.text.fill', stylers: [{ color: '#d59563' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#17263c' }] },
  { featureType: 'water', elementType: 'labels.text.fill', stylers: [{ color: '#515c6d' }] },
  { featureType: 'water', elementType: 'labels.text.stroke', stylers: [{ color: '#17263c' }] }
];

const splitTags = (value) => String(value || '')
  .split(',')
  .map((tag) => tag.trim())
  .filter(Boolean);

const MENU_PROPS = {
  PaperProps: {
    style: {
      maxHeight: 320,
      width: 280
    }
  }
};

const REGION_OPTIONS = ['Australia', 'Canada-East', 'Canada-West', 'New Zealand', 'US-East', 'US-West'];
const REGION_ALIASES = {
  'aus': 'Australia',
  'australia': 'Australia',
  'ca-east': 'Canada-East',
  'canada-east': 'Canada-East',
  'ca-west': 'Canada-West',
  'canada-west': 'Canada-West',
  'nz': 'New Zealand',
  'new zealand': 'New Zealand',
  'us-east': 'US-East',
  'us-west': 'US-West',
};

const SITE_STATUS_ALIASES = {
  'running': 'Running',
  'temporary closure': 'Temporary Closure',
  'permanent closure': 'Permanent Closure',
  'under construction': 'Under Construction',
};

const canonicalize = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '');

const normalizeLogoFileName = (value) => String(value || '')
  .trim()
  .toLowerCase()
  .replace(/\.[a-z0-9]+$/i, '')
  .replace(/&/g, ' and ')
  .replace(/[^a-z0-9]+/g, '');

const logoEntries = logoContext.keys().map((key) => {
  const src = logoContext(key);
  const fileName = key.replace('./', '');
  return {
    fileName,
    normalizedFileName: normalizeLogoFileName(fileName),
    src: src.default || src,
  };
});

const resolveLogoSrc = (logoFileName) => {
  const normalizedLogo = normalizeLogoFileName(logoFileName);
  if (!normalizedLogo) {
    return null;
  }

  const match = logoEntries.find((entry) => entry.normalizedFileName === normalizedLogo);
  return match?.src || null;
};

const normalizeRegion = (value) => {
  const raw = String(value || '').trim();
  const aliasKey = raw.toLowerCase();
  return REGION_ALIASES[aliasKey] || raw;
};

const normalizeSiteStatus = (value) => {
  const raw = String(value || '').trim();
  const aliasKey = raw.toLowerCase();
  return SITE_STATUS_ALIASES[aliasKey] || raw;
};

const normalizeTag = (value) => String(value || '').trim().toLowerCase();

const normalizeCustomerValue = (value) => {
  if (value === true || value === 1) return '1';
  if (value === false || value === 0) return '0';

  const normalized = String(value ?? '').trim().toLowerCase();
  if (['1', 'true', 'yes', 'y'].includes(normalized)) return '1';
  if (['0', 'false', 'no', 'n'].includes(normalized)) return '0';
  return '';
};

const toOptionList = (values) => values
  .filter(Boolean)
  .map((label) => ({ value: canonicalize(label), label }))
  .filter((option, index, array) => array.findIndex((item) => item.value === option.value) === index);

const RADIUS_OPTIONS_KM = [25, 50, 100, 200, 300];

const toRadians = (value) => value * (Math.PI / 180);

const calculateDistanceKm = (fromLat, fromLng, toLat, toLng) => {
  const earthRadiusKm = 6371;
  const dLat = toRadians(toLat - fromLat);
  const dLng = toRadians(toLng - fromLng);
  const a = Math.sin(dLat / 2) ** 2
    + Math.cos(toRadians(fromLat)) * Math.cos(toRadians(toLat)) * Math.sin(dLng / 2) ** 2;
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return earthRadiusKm * c;
};

const formatCoordinate = (value) => Number(value).toFixed(4);
const getLocationName = (location) => [location?.CompanyName, location?.DivisionName].filter(Boolean).join(' ');
const getDisplayTripName = (value) => String(value || '').trim() || 'My Trip';
const getTripStopKey = (location) => {
  if (location?.TripStopKey) {
    return location.TripStopKey;
  }

  if (location?.type && Number.isFinite(parseFloat(location?.latitude)) && Number.isFinite(parseFloat(location?.longitude))) {
    return `airport-${location.ident || location.iataCode || location.name}-${location.latitude}-${location.longitude}`;
  }

  if (location?.LocationID !== undefined && location?.LocationID !== null) {
    return `location-${location.LocationID}`;
  }

  return `point-${location?.Latitude ?? location?.latitude}-${location?.Longitude ?? location?.longitude}`;
};

const getLocationCoordinates = (location) => {
  const latitude = parseFloat(location?.Latitude ?? location?.latitude);
  const longitude = parseFloat(location?.Longitude ?? location?.longitude);

  if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
    return null;
  }

  return { latitude, longitude };
};

const createTripPoint = (location) => {
  const coordinates = getLocationCoordinates(location);
  if (!coordinates) {
    return null;
  }

  return {
    TripStopKey: getTripStopKey(location),
    LocationID: location?.LocationID,
    CompanyName: location?.CompanyName || location?.name || '',
    DivisionName: location?.DivisionName || location?.iataCode || location?.ident || '',
    Latitude: coordinates.latitude,
    Longitude: coordinates.longitude,
    Region: location?.Region || location?.region || '',
    SiteStatus: location?.SiteStatus || (location?.type ? 'Airport' : ''),
    SupportStatus: location?.SupportStatus ?? null,
    Address: location?.Address || '',
    City: location?.City || location?.municipality || '',
    State: location?.State || '',
    Postal: location?.Postal || '',
    Country: location?.Country || location?.country || '',
    Notes: location?.Notes || '',
    AirportType: location?.AirportType || location?.type || '',
    IataCode: location?.IataCode || location?.iataCode || '',
    Ident: location?.Ident || location?.ident || '',
    DistanceFromSelectedKm: location?.DistanceFromSelectedKm ?? location?.distanceKm ?? null,
    isOrigin: Boolean(location?.isOrigin),
  };
};

const Maps = () => {
  const { theme } = useTheme();
  const routeLocation = useLocation();
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || '';
  const { isLoaded: isMapLoaded, loadError } = useJsApiLoader({
    id: 'tracking-system-google-maps',
    googleMapsApiKey,
  });
  const [locationData, setLocationData] = useState([]);
  const [zoneData, setZoneData] = useState([]);
  const [tagData, setTagData] = useState([]);
  const [tagCategoryData, setTagCategoryData] = useState([]);
  const [selectedRegions, setSelectedRegions] = useState([]);
  const [selectedSiteStatuses, setSelectedSiteStatuses] = useState([]);
  const [selectedCustomerValue, setSelectedCustomerValue] = useState('');
  const [selectedCompanies, setSelectedCompanies] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [selectedTagCategories, setSelectedTagCategories] = useState([]);
  const [selectedSpecies, setSelectedSpecies] = useState([]);
  const [selectedOperationTypes, setSelectedOperationTypes] = useState([]);
  const [selectedLocationDetails, setSelectedLocationDetails] = useState(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const [detailsError, setDetailsError] = useState('');
  const [mapInstance, setMapInstance] = useState(null);
  const [nearbySearch, setNearbySearch] = useState(null);
  const [nearbyAirports, setNearbyAirports] = useState([]);
  const [nearbyAirportsLoading, setNearbyAirportsLoading] = useState(false);
  const [nearbyAirportsError, setNearbyAirportsError] = useState('');
  const [tripName, setTripName] = useState('My Trip');
  const [tripOrigin, setTripOrigin] = useState(null);
  const [tripStops, setTripStops] = useState([]);
  const [returnToOrigin, setReturnToOrigin] = useState(false);
  const [tripReportOpen, setTripReportOpen] = useState(false);
  const [sendingTripReport, setSendingTripReport] = useState(false);
  const [tripReportMessage, setTripReportMessage] = useState('');
  const [hoveredNearbyLocationId, setHoveredNearbyLocationId] = useState(null);
  const nearbyCircleRef = useRef(null);
  const tripPolylineRef = useRef(null);

  const replaceNearbySearch = (nextNearbySearch) => {
    setNearbySearch(null);
    window.setTimeout(() => {
      setNearbySearch(nextNearbySearch);
    }, 0);
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const locationResponse = await fetch(`/api/map-locations`);
        if (!locationResponse.ok) {
          throw new Error(`Map location request failed with ${locationResponse.status}`);
        }

        const locationResult = await locationResponse.json();
        if (Array.isArray(locationResult)) {
          setLocationData(locationResult);
        } else {
          console.error('Invalid or no location data received:', locationResult);
        }
      } catch (error) {
        console.error('Error fetching map location data:', error);
      }

      try {
        const zonesResponse = await fetch(`/api/zones`);
        if (!zonesResponse.ok) {
          throw new Error(`Zone request failed with ${zonesResponse.status}`);
        }

        const zonesResult = await zonesResponse.json();
        if (Array.isArray(zonesResult)) {
          setZoneData(zonesResult);
        } else {
          console.error('Invalid or no zone data received:', zonesResult);
        }
      } catch (error) {
        console.error('Error fetching zone data:', error);
        setZoneData([]);
      }

      try {
        const tagsResponse = await fetch(`/api/tags`);
        if (!tagsResponse.ok) {
          throw new Error(`Tag request failed with ${tagsResponse.status}`);
        }

        const tagsResult = await tagsResponse.json();
        if (Array.isArray(tagsResult)) {
          setTagData(tagsResult);
        } else {
          console.error('Invalid or no tag data received:', tagsResult);
        }
      } catch (error) {
        console.error('Error fetching tag data:', error);
        setTagData([]);
      }

      try {
        const tagCategoriesResponse = await fetch(`/api/tags/categories`);
        if (!tagCategoriesResponse.ok) {
          throw new Error(`Tag category request failed with ${tagCategoriesResponse.status}`);
        }

        const tagCategoriesResult = await tagCategoriesResponse.json();
        if (Array.isArray(tagCategoriesResult)) {
          setTagCategoryData(tagCategoriesResult);
        } else {
          console.error('Invalid or no tag category data received:', tagCategoriesResult);
        }
      } catch (error) {
        console.error('Error fetching tag category data:', error);
        setTagCategoryData([]);
      }
    };

    fetchData();
  }, []);

  const locationZoneMeta = useMemo(() => {
    const meta = new Map();

    (zoneData || []).forEach((zone) => {
      const locationId = Number(zone.LocationID);
      if (!Number.isFinite(locationId)) {
        return;
      }

      if (!meta.has(locationId)) {
        meta.set(locationId, {
          zones: new Set(),
          tags: new Set(),
          tagCategoryIds: new Set(),
        });
      }

      const current = meta.get(locationId);
      const zoneName = String(zone.ZoneName || '').trim();
      if (zoneName) {
        current.zones.add(zoneName);
      }

      splitTags(zone.Tags).forEach((tag) => {
        const normalizedTag = normalizeTag(tag);
        current.tags.add(normalizedTag);

        const matchingTag = (tagData || []).find((item) => normalizeTag(item.Name) === normalizedTag);
        const tagCategoryId = matchingTag?.TagCategoryID ?? matchingTag?.TagCategoryId;
        if (tagCategoryId !== undefined && tagCategoryId !== null) {
          current.tagCategoryIds.add(String(tagCategoryId));
        }
      });
    });

    return meta;
  }, [tagData, zoneData]);

  const regionOptions = useMemo(() => {
    const fromData = [...new Set((locationData || []).map((item) => normalizeRegion(item.Region)).filter(Boolean))];
    return toOptionList([...REGION_OPTIONS, ...fromData]);
  }, [locationData]);

  const zoneOptions = useMemo(() => {
    return [...new Set((zoneData || [])
      .map((item) => String(item.ZoneName || '').trim())
      .filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [zoneData]);

  const companyOptions = useMemo(() => {
    return [...new Set((locationData || []).map((item) => String(item.CompanyName || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [locationData]);

  const tagOptions = useMemo(() => {
    const fromZones = (zoneData || []).flatMap((item) => splitTags(item.Tags));
    const fromTags = (tagData || []).map((item) => item.Name).filter(Boolean);
    return [...new Set([...fromZones, ...fromTags])].sort((a, b) => a.localeCompare(b));
  }, [tagData, zoneData]);

  const tagCategoryOptions = useMemo(() => {
    return (tagCategoryData || [])
      .map((item) => {
        const value = item.TagCategoryID ?? item.id;
        if (value === undefined || value === null || !item.Name) {
          return null;
        }

        return {
          value: String(value),
          label: item.Name,
        };
      })
      .filter(Boolean)
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [tagCategoryData]);

  const speciesOptions = useMemo(() => {
    return [...new Set((locationData || []).flatMap((item) => Array.isArray(item.Species) ? item.Species : []).map((value) => String(value || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [locationData]);

  const operationTypeOptions = useMemo(() => {
    return [...new Set((locationData || []).flatMap((item) => Array.isArray(item.OperationTypes) ? item.OperationTypes : []).map((value) => String(value || '').trim()).filter(Boolean))]
      .sort((a, b) => a.localeCompare(b));
  }, [locationData]);

  const filteredLocations = useMemo(() => {
    return (locationData || []).filter((location) => {
      const locationId = Number(location.LocationID);
      const meta = locationZoneMeta.get(locationId) || { zones: new Set(), tags: new Set(), tagCategoryIds: new Set() };
      const latitude = parseFloat(location.Latitude);
      const longitude = parseFloat(location.Longitude);
      const normalizedRegion = canonicalize(normalizeRegion(location.Region));
      const normalizedSiteStatus = canonicalize(normalizeSiteStatus(location.SiteStatus));
      const normalizedCustomer = normalizeCustomerValue(location.SiCamCustomer);
      const species = Array.isArray(location.Species)
        ? location.Species.map((value) => String(value || '').trim()).filter(Boolean)
        : [];
      const operationTypes = Array.isArray(location.OperationTypes)
        ? location.OperationTypes.map((value) => String(value || '').trim()).filter(Boolean)
        : [];

      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return false;
      }

      if (selectedRegions.length && !selectedRegions.includes(normalizedRegion)) {
        return false;
      }

      if (selectedSiteStatuses.length && !selectedSiteStatuses.includes(normalizedSiteStatus)) {
        return false;
      }

      if (selectedCustomerValue !== '' && normalizedCustomer !== selectedCustomerValue) {
        return false;
      }

      if (selectedCompanies.length && !selectedCompanies.includes(String(location.CompanyName || '').trim())) {
        return false;
      }

      if (selectedZones.length && zoneData.length > 0 && !selectedZones.some((value) => meta.zones.has(String(value)))) {
        return false;
      }

      if (selectedTags.length && zoneData.length > 0 && !selectedTags.some((value) => meta.tags.has(normalizeTag(value)))) {
        return false;
      }

      if (selectedTagCategories.length && zoneData.length > 0 && !selectedTagCategories.some((value) => meta.tagCategoryIds.has(String(value)))) {
        return false;
      }

      if (selectedSpecies.length && !selectedSpecies.some((value) => species.includes(value))) {
        return false;
      }

      if (selectedOperationTypes.length && !selectedOperationTypes.some((value) => operationTypes.includes(value))) {
        return false;
      }

      return true;
    });
  }, [locationData, locationZoneMeta, selectedCompanies, selectedCustomerValue, selectedOperationTypes, selectedRegions, selectedSiteStatuses, selectedSpecies, selectedTagCategories, selectedTags, selectedZones, zoneData.length]);

  const textColor = theme === 'dark' ? '#fff' : '#000';

  const clearFilters = () => {
    setSelectedRegions([]);
    setSelectedSiteStatuses([]);
    setSelectedCustomerValue('');
    setSelectedCompanies([]);
    setSelectedZones([]);
    setSelectedTags([]);
    setSelectedTagCategories([]);
    setSelectedSpecies([]);
    setSelectedOperationTypes([]);
  };

  const nearbyLocations = useMemo(() => {
    if (!nearbySearch) {
      return [];
    }

    const sourceLocationId = Number(nearbySearch.location.LocationID);

    return (locationData || [])
      .filter((location) => !Number.isFinite(sourceLocationId) || Number(location.LocationID) !== sourceLocationId)
      .map((location) => {
        const latitude = parseFloat(location.Latitude);
        const longitude = parseFloat(location.Longitude);
        if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
          return null;
        }

        const distanceKm = calculateDistanceKm(
          nearbySearch.location.latitude,
          nearbySearch.location.longitude,
          latitude,
          longitude,
        );

        return {
          ...location,
          distanceKm,
        };
      })
      .filter((location) => location && location.distanceKm <= nearbySearch.radiusKm)
      .sort((left, right) => left.distanceKm - right.distanceKm);
  }, [locationData, nearbySearch]);

  useEffect(() => {
    if (!nearbySearch) {
      setNearbyAirports([]);
      setNearbyAirportsLoading(false);
      setNearbyAirportsError('');
      return undefined;
    }

    const controller = new AbortController();

    const loadNearbyAirports = async () => {
      setNearbyAirportsLoading(true);
      setNearbyAirportsError('');

      try {
        const query = new URLSearchParams({
          lat: String(nearbySearch.location.latitude),
          lng: String(nearbySearch.location.longitude),
          radiusKm: String(nearbySearch.radiusKm),
          types: 'large,medium',
        });
        const response = await fetch(`/api/airports/nearby?${query.toString()}`, {
          signal: controller.signal,
        });
        const result = await response.json().catch(() => ([]));
        if (!response.ok) {
          throw new Error(result?.message || 'Unable to load nearby airports.');
        }

        setNearbyAirports(Array.isArray(result) ? result : []);
      } catch (error) {
        if (error.name === 'AbortError') {
          return;
        }

        setNearbyAirports([]);
        setNearbyAirportsError(error.message || 'Unable to load nearby airports.');
      } finally {
        if (!controller.signal.aborted) {
          setNearbyAirportsLoading(false);
        }
      }
    };

    loadNearbyAirports();

    return () => {
      controller.abort();
    };
  }, [nearbySearch]);

  const tripRoutePath = useMemo(() => {
    const points = [];
    const originCoordinates = getLocationCoordinates(tripOrigin);

    if (originCoordinates) {
      points.push({ lat: originCoordinates.latitude, lng: originCoordinates.longitude });
    }

    tripStops.forEach((location) => {
      const coordinates = getLocationCoordinates(location);
      if (coordinates) {
        points.push({ lat: coordinates.latitude, lng: coordinates.longitude });
      }
    });

    if (returnToOrigin && originCoordinates && tripStops.length) {
      points.push({ lat: originCoordinates.latitude, lng: originCoordinates.longitude });
    }

    return points;
  }, [returnToOrigin, tripOrigin, tripStops]);

  const tripReportRows = useMemo(() => {
    const rows = [];
    let previousPoint = tripOrigin ? createTripPoint({ ...tripOrigin, isOrigin: true }) : null;

    tripStops.forEach((location, index) => {
      const currentPoint = createTripPoint(location);
      const currentCoords = getLocationCoordinates(currentPoint);
      const previousCoords = getLocationCoordinates(previousPoint);
      const segmentDistanceKm = currentCoords && previousCoords
        ? calculateDistanceKm(previousCoords.latitude, previousCoords.longitude, currentCoords.latitude, currentCoords.longitude)
        : null;

      rows.push({
        ...currentPoint,
        stopNumber: index + 1,
        segmentDistanceKm,
        rowType: 'stop',
      });

      previousPoint = currentPoint;
    });

    if (returnToOrigin && previousPoint && tripOrigin && tripStops.length) {
      const originPoint = createTripPoint({ ...tripOrigin, isOrigin: true });
      const currentCoords = getLocationCoordinates(originPoint);
      const previousCoords = getLocationCoordinates(previousPoint);
      const segmentDistanceKm = currentCoords && previousCoords
        ? calculateDistanceKm(previousCoords.latitude, previousCoords.longitude, currentCoords.latitude, currentCoords.longitude)
        : null;

      rows.push({
        ...originPoint,
        stopNumber: rows.length + 1,
        segmentDistanceKm,
        rowType: 'return',
      });
    }

    return rows;
  }, [returnToOrigin, tripOrigin, tripStops]);

  const totalTripDistanceKm = useMemo(() => {
    return tripReportRows.reduce((total, stop) => total + (stop.segmentDistanceKm || 0), 0);
  }, [tripReportRows]);

  const nearestAirports = useMemo(() => {
    const nearestLarge = nearbyAirports.find((airport) => airport.type === 'large_airport') || null;
    const nearestMedium = nearbyAirports.find((airport) => airport.type === 'medium_airport') || null;
    return [nearestLarge, nearestMedium].filter(Boolean);
  }, [nearbyAirports]);

  useEffect(() => {
    if (!mapInstance || !nearbySearch || !window.google?.maps) {
      return;
    }

    const latDelta = nearbySearch.radiusKm / 111.32;
    const lngDelta = nearbySearch.radiusKm / (111.32 * Math.max(Math.cos(toRadians(nearbySearch.location.latitude)), 0.2));
    const bounds = new window.google.maps.LatLngBounds(
      {
        lat: nearbySearch.location.latitude - latDelta,
        lng: nearbySearch.location.longitude - lngDelta,
      },
      {
        lat: nearbySearch.location.latitude + latDelta,
        lng: nearbySearch.location.longitude + lngDelta,
      },
    );

    mapInstance.fitBounds(bounds, 60);
  }, [mapInstance, nearbySearch]);

  useEffect(() => {
    if (nearbyCircleRef.current) {
      nearbyCircleRef.current.setMap(null);
      nearbyCircleRef.current = null;
    }

    if (!mapInstance || !nearbySearch || !window.google?.maps) {
      return undefined;
    }

    nearbyCircleRef.current = new window.google.maps.Circle({
      map: mapInstance,
      center: {
        lat: nearbySearch.location.latitude,
        lng: nearbySearch.location.longitude,
      },
      radius: nearbySearch.radiusKm * 1000,
      fillColor: '#2563eb',
      fillOpacity: 0.12,
      strokeColor: '#2563eb',
      strokeOpacity: 0.9,
      strokeWeight: 2,
    });

    return () => {
      if (nearbyCircleRef.current) {
        nearbyCircleRef.current.setMap(null);
        nearbyCircleRef.current = null;
      }
    };
  }, [mapInstance, nearbySearch]);

  useEffect(() => {
    if (tripPolylineRef.current) {
      tripPolylineRef.current.setMap(null);
      tripPolylineRef.current = null;
    }

    if (!mapInstance || tripRoutePath.length < 2 || !window.google?.maps) {
      return undefined;
    }

    tripPolylineRef.current = new window.google.maps.Polyline({
      map: mapInstance,
      path: tripRoutePath,
      strokeColor: '#111111',
      strokeOpacity: 0.95,
      strokeWeight: 4,
      geodesic: true,
    });

    return () => {
      if (tripPolylineRef.current) {
        tripPolylineRef.current.setMap(null);
        tripPolylineRef.current = null;
      }
    };
  }, [mapInstance, tripRoutePath]);

  const openNearbySearch = (location, radiusKm) => {
    const latitude = parseFloat(location.Latitude);
    const longitude = parseFloat(location.Longitude);
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    replaceNearbySearch({
      location: {
        ...createTripPoint(location),
        latitude,
        longitude,
      },
      radiusKm,
    });
    setDetailsDialogOpen(false);
  };

  const openNearbySearchFromPoint = (latitude, longitude, radiusKm = 50) => {
    if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    replaceNearbySearch({
      location: {
        ...createTripPoint({
          CompanyName: 'Selected Map Point',
          DivisionName: `${formatCoordinate(latitude)}, ${formatCoordinate(longitude)}`,
          Latitude: latitude,
          Longitude: longitude,
        }),
        CompanyName: 'Selected Map Point',
        DivisionName: `${formatCoordinate(latitude)}, ${formatCoordinate(longitude)}`,
        latitude,
        longitude,
      },
      radiusKm,
    });
    setDetailsDialogOpen(false);
  };

  const handleMapRightClick = (event) => {
    const latitude = event?.latLng?.lat?.();
    const longitude = event?.latLng?.lng?.();
    openNearbySearchFromPoint(latitude, longitude);
  };

  const addTripStop = (location) => {
    const tripPoint = createTripPoint(location);
    if (!tripPoint) {
      return;
    }

    setTripStops((current) => {
      if (current.some((item) => getTripStopKey(item) === getTripStopKey(tripPoint))) {
        return current;
      }

      return [...current, tripPoint];
    });
    setTripReportMessage('');
    setTripOrigin((current) => {
      if (current) {
        return current;
      }

      return nearbySearch ? createTripPoint(nearbySearch.location) : current;
    });
  };

  const removeTripStop = (locationId) => {
    setTripStops((current) => {
      const next = current.filter((item) => getTripStopKey(item) !== locationId);
      if (!next.length) {
        setTripOrigin(null);
        setReturnToOrigin(false);
      }
      return next;
    });
  };

  const moveTripStop = (index, direction) => {
    setTripStops((current) => {
      const nextIndex = index + direction;
      if (nextIndex < 0 || nextIndex >= current.length) {
        return current;
      }

      const next = [...current];
      const [moved] = next.splice(index, 1);
      next.splice(nextIndex, 0, moved);
      return next;
    });
  };

  const clearTripStops = () => {
    setTripStops([]);
    setTripName('My Trip');
    setTripOrigin(null);
    setReturnToOrigin(false);
    setTripReportOpen(false);
    setTripReportMessage('');
    setHoveredNearbyLocationId(null);
  };

  const handleNearbyLocationHover = (locationId) => {
    setHoveredNearbyLocationId(locationId);
  };

  const clearNearbyLocationHover = () => {
    setHoveredNearbyLocationId(null);
  };

  const emailTripReport = async () => {
    if (!tripReportRows.length) {
      return;
    }

    setSendingTripReport(true);
    setTripReportMessage('');

    try {
      const response = await fetch('/api/maps/trip-report/email', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          tripName,
          origin: tripOrigin,
          stops: tripStops,
          returnToOrigin,
          totalTripDistanceKm,
          reportRows: tripReportRows,
        }),
      });
      const rawText = await response.text();
      let payload = {};
      try {
        payload = rawText ? JSON.parse(rawText) : {};
      } catch (_error) {
        payload = {};
      }
      if (!response.ok) {
        throw new Error(payload.message || rawText || `Unable to email trip report (${response.status}).`);
      }

      setTripReportMessage(payload.message || 'Trip report emailed successfully.');
    } catch (error) {
      setTripReportMessage(error.message || 'Unable to email trip report.');
    } finally {
      setSendingTripReport(false);
    }
  };

  const handleMarkerClick = async (location) => {
    setDetailsDialogOpen(true);
    setDetailsLoading(true);
    setDetailsError('');

    try {
      const response = await fetch(`/api/map-locations/${location.LocationID}/details`);
      const result = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(result?.message || 'Unable to load location details.');
      }

      setSelectedLocationDetails(result);
    } catch (error) {
      setSelectedLocationDetails(null);
      setDetailsError(error.message || 'Unable to load location details.');
    } finally {
      setDetailsLoading(false);
    }
  };

  const handleCloseDetailsDialog = () => {
    setDetailsDialogOpen(false);
    setSelectedLocationDetails(null);
    setDetailsError('');
    setDetailsLoading(false);
  };

  const formatAddress = (location) => {
    const addressParts = [
      location?.Address,
      location?.address,
      location?.StreetAddress,
      location?.AddressLine1,
      location?.City,
      location?.city,
      location?.State,
      location?.state,
      location?.Postal,
      location?.postal,
      location?.Country,
      location?.country,
    ]
      .map((value) => String(value || '').trim())
      .filter(Boolean);

    return [...new Set(addressParts)].join(', ');
  };

  const formatContactName = (contact) => [contact?.FirstName, contact?.LastName].filter(Boolean).join(' ');

  const nearbyPanelBackground = theme === 'dark' ? 'rgba(16, 25, 36, 0.94)' : 'rgba(255, 253, 248, 0.96)';
  const nearbyPanelBorder = theme === 'dark' ? 'rgba(148, 163, 184, 0.24)' : 'rgba(15, 23, 42, 0.12)';
  const panelSubtleText = theme === 'dark' ? 'rgba(226, 232, 240, 0.84)' : 'rgba(31, 41, 55, 0.78)';
  const panelActionText = theme === 'dark' ? '#f8fbff' : '#111827';
  const sectionTitleColor = theme === 'dark' ? '#f8fbff' : '#0f172a';
  const sectionTitleBackground = theme === 'dark' ? 'rgba(30, 41, 59, 0.92)' : 'rgba(241, 245, 249, 0.95)';
  const nearbyLocationsSectionBackground = theme === 'dark' ? 'rgba(15, 23, 42, 0.44)' : 'rgba(255, 255, 255, 0.78)';
  const nearbyAirportsSectionBackground = theme === 'dark' ? 'rgba(24, 24, 27, 0.48)' : 'rgba(248, 250, 252, 0.92)';
  const tripPlannerSectionBackground = theme === 'dark' ? 'rgba(22, 101, 52, 0.16)' : 'rgba(240, 253, 244, 0.92)';
  const focusLocationId = new URLSearchParams(routeLocation.search).get('focusLocationId');

  useEffect(() => {
    if (!mapInstance || !focusLocationId) {
      return;
    }

    const targetLocation = (locationData || []).find((item) => Number(item.LocationID) === Number(focusLocationId));
    const latitude = parseFloat(targetLocation?.Latitude);
    const longitude = parseFloat(targetLocation?.Longitude);

    if (!targetLocation || !Number.isFinite(latitude) || !Number.isFinite(longitude)) {
      return;
    }

    mapInstance.panTo({ lat: latitude, lng: longitude });
    mapInstance.setZoom(12);
  }, [focusLocationId, locationData, mapInstance]);

  return (
    <div className={`${styles.all} ${styles[theme]} ${styles.mapsPage}`}>
      <div className={styles.mapsSection}>
        <div className={`${styles.mapToolbar} ${styles[theme]}`}>
          <div className={styles.mapToolbarTitle}>
            <Typography variant="h6" sx={{ fontSize: 18, color: textColor }}>
              Map Filters
            </Typography>
            <div className={styles.filterSummary}>
              <strong>{filteredLocations.length}</strong> site{filteredLocations.length === 1 ? '' : 's'} shown
            </div>
          </div>

          <FormControl size="small" className={`${styles.mapToolbarControl} ${styles.mapToolbarControlCompact} ${styles.mapToolbarControlCompactWide}`}>
            <InputLabel id="region-filter-label">Region</InputLabel>
            <Select
              labelId="region-filter-label"
              multiple
              value={selectedRegions}
              onChange={(event) => setSelectedRegions(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
              input={<OutlinedInput label="Region" />}
              renderValue={(selected) => (
                <div className={styles.filterChips}>
                  {selected.map((value) => <Chip key={value} size="small" label={regionOptions.find((option) => option.value === value)?.label || value} />)}
                </div>
              )}
              MenuProps={MENU_PROPS}
            >
              {regionOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" className={styles.mapToolbarControl}>
            <InputLabel id="customer-filter-label">SiCam Customer</InputLabel>
            <Select
              labelId="customer-filter-label"
              label="SiCam Customer"
              value={selectedCustomerValue}
              onChange={(event) => setSelectedCustomerValue(event.target.value)}
            >
              <MenuItem value="">All</MenuItem>
              <MenuItem value="1">Yes</MenuItem>
              <MenuItem value="0">No</MenuItem>
            </Select>
          </FormControl>

          <FormControl size="small" className={styles.mapToolbarControl}>
            <InputLabel id="company-filter-label">Company</InputLabel>
            <Select
              labelId="company-filter-label"
              multiple
              value={selectedCompanies}
              onChange={(event) => setSelectedCompanies(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
              input={<OutlinedInput label="Company" />}
              renderValue={(selected) => (
                <div className={styles.filterChips}>
                  {selected.map((value) => <Chip key={value} size="small" label={value} />)}
                </div>
              )}
              MenuProps={MENU_PROPS}
            >
              {companyOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" className={styles.mapToolbarControl}>
            <InputLabel id="zone-filter-label">Zones</InputLabel>
            <Select
              labelId="zone-filter-label"
              multiple
              value={selectedZones}
              onChange={(event) => setSelectedZones(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
              input={<OutlinedInput label="Zones" />}
              renderValue={(selected) => (
                <div className={styles.filterChips}>
                  {selected.map((value) => <Chip key={value} size="small" label={value} />)}
                </div>
              )}
              MenuProps={MENU_PROPS}
            >
              {zoneOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" className={`${styles.mapToolbarControl} ${styles.mapToolbarControlCompact}`}>
            <InputLabel id="tag-filter-label">Tags</InputLabel>
            <Select
              labelId="tag-filter-label"
              multiple
              value={selectedTags}
              onChange={(event) => setSelectedTags(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
              input={<OutlinedInput label="Tags" />}
              renderValue={(selected) => (
                <div className={styles.filterChips}>
                  {selected.map((value) => <Chip key={value} size="small" label={value} />)}
                </div>
              )}
              MenuProps={MENU_PROPS}
            >
              {tagOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" className={styles.mapToolbarControl}>
            <InputLabel id="tag-category-filter-label">Tag Category</InputLabel>
            <Select
              labelId="tag-category-filter-label"
              multiple
              value={selectedTagCategories}
              onChange={(event) => setSelectedTagCategories(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
              input={<OutlinedInput label="Tag Category" />}
              renderValue={(selected) => (
                <div className={styles.filterChips}>
                  {selected.map((value) => <Chip key={value} size="small" label={tagCategoryOptions.find((option) => option.value === value)?.label || value} />)}
                </div>
              )}
              MenuProps={MENU_PROPS}
            >
              {tagCategoryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  <ListItemText primary={option.label} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" className={`${styles.mapToolbarControl} ${styles.mapToolbarControlCompact}`}>
            <InputLabel id="species-filter-label">Species</InputLabel>
            <Select
              labelId="species-filter-label"
              multiple
              value={selectedSpecies}
              onChange={(event) => setSelectedSpecies(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
              input={<OutlinedInput label="Species" />}
              renderValue={(selected) => (
                <div className={styles.filterChips}>
                  {selected.map((value) => <Chip key={value} size="small" label={value} />)}
                </div>
              )}
              MenuProps={MENU_PROPS}
            >
              {speciesOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" className={styles.mapToolbarControl}>
            <InputLabel id="operation-type-filter-label">Operation Type</InputLabel>
            <Select
              labelId="operation-type-filter-label"
              multiple
              value={selectedOperationTypes}
              onChange={(event) => setSelectedOperationTypes(typeof event.target.value === 'string' ? event.target.value.split(',') : event.target.value)}
              input={<OutlinedInput label="Operation Type" />}
              renderValue={(selected) => (
                <div className={styles.filterChips}>
                  {selected.map((value) => <Chip key={value} size="small" label={value} />)}
                </div>
              )}
              MenuProps={MENU_PROPS}
            >
              {operationTypeOptions.map((option) => (
                <MenuItem key={option} value={option}>
                  <ListItemText primary={option} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button size="small" variant="outlined" onClick={clearFilters}>Clear</Button>
        </div>

        <div className={styles.mapCanvasWrap}>
          {!googleMapsApiKey ? (
            <Alert severity="warning">Google Maps is unavailable because `REACT_APP_GOOGLE_MAPS_API_KEY` is missing.</Alert>
          ) : loadError ? (
            <Alert severity="error">Google Maps failed to load. The rest of the app is still available.</Alert>
          ) : !isMapLoaded ? (
            <Typography variant="body2">Loading map...</Typography>
          ) : (
            <GoogleMap
              mapContainerStyle={containerStyle}
              center={center}
              zoom={3}
              onLoad={(map) => setMapInstance(map)}
              onRightClick={handleMapRightClick}
              options={{
                styles: theme === 'dark' ? darkStyle : null,
                minZoom: 2,
              }}
            >
              {nearbySearch ? (
                <>
                  <Marker
                    key={`nearby-center-${nearbySearch.location.latitude}-${nearbySearch.location.longitude}`}
                    position={{ lat: nearbySearch.location.latitude, lng: nearbySearch.location.longitude }}
                    icon={defaultMarkerIcon}
                    title="Nearby search center"
                  />
                </>
              ) : null}

              {filteredLocations.map((location) => {
                const latitude = parseFloat(location.Latitude);
                const longitude = parseFloat(location.Longitude);
                const isCustomer = location.SiCamCustomer === true || location.SiCamCustomer === 1;
                const isHoveredNearbyLocation = Number(hoveredNearbyLocationId) === Number(location.LocationID);
                const titleParts = [
                  `${location.CompanyName} ${location.DivisionName}`,
                  location.SiteStatus ? `Site Status: ${location.SiteStatus}` : null,
                  location.Region ? `Region: ${location.Region}` : null,
                  Array.isArray(location.Species) && location.Species.length ? `Species: ${location.Species.join(', ')}` : null,
                  Array.isArray(location.OperationTypes) && location.OperationTypes.length ? `Operation Type: ${location.OperationTypes.join(', ')}` : null,
                  `SiCam Customer: ${isCustomer ? 'Yes' : 'No'}`,
                  location.SupportStatus ? `Support: ${supportStatusDescriptions[location.SupportStatus] || location.SupportStatus}` : null,
                ].filter(Boolean);

                return (
                  <React.Fragment key={location.LocationID}>
                    <Marker
                      position={{ lat: latitude, lng: longitude }}
                      icon={icons[location.SupportStatus] || defaultMarkerIcon}
                      title={titleParts.join('\n')}
                      onClick={() => handleMarkerClick(location)}
                      animation={isHoveredNearbyLocation ? window.google.maps.Animation.BOUNCE : undefined}
                      zIndex={isHoveredNearbyLocation ? 1000 : undefined}
                    />
                  </React.Fragment>
                );
              })}

              {nearbySearch && nearestAirports.map((airport) => (
                <Marker
                  key={`airport-marker-${airport.ident}-${airport.latitude}-${airport.longitude}`}
                  position={{ lat: airport.latitude, lng: airport.longitude }}
                  icon={nearestAirportMarkerIcon}
                  zIndex={1200}
                  title={[
                    airport.type === 'large_airport' ? 'Nearest Large Airport' : 'Nearest Medium Airport',
                    airport.name,
                    airport.iataCode ? `Code: ${airport.iataCode}` : airport.ident ? `Ident: ${airport.ident}` : null,
                    airport.municipality ? `City: ${airport.municipality}` : null,
                    airport.type === 'large_airport' ? 'Large Airport' : 'Medium Airport',
                    airport.distanceKm ? `${airport.distanceKm.toFixed(1)} km away` : null,
                  ].filter(Boolean).join('\n')}
                />
              ))}
            </GoogleMap>
          )}

          {nearbySearch || tripStops.length ? (
            <Box
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                width: 380,
                maxHeight: 'calc(100% - 32px)',
                overflowY: 'auto',
                borderRadius: '16px',
                p: 2,
                backgroundColor: nearbyPanelBackground,
                color: theme === 'dark' ? '#f8fbff' : '#1f2937',
                border: `1px solid ${nearbyPanelBorder}`,
                boxShadow: '0 16px 40px rgba(15, 23, 42, 0.18)',
                zIndex: 2,
              }}
            >
              {nearbySearch ? (
                <Box
                  sx={{
                    mb: 1.75,
                    p: 1.5,
                    borderRadius: '14px',
                    backgroundColor: nearbyLocationsSectionBackground,
                    border: `1px solid ${nearbyPanelBorder}`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 1.1,
                      py: 0.45,
                      mb: 1,
                      borderRadius: '999px',
                      backgroundColor: sectionTitleBackground,
                      color: sectionTitleColor,
                      boxShadow: '0 6px 18px rgba(15, 23, 42, 0.12)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Nearby Locations
                    </Typography>
                  </Box>
                  <Typography variant="body2" sx={{ mb: 1, color: panelSubtleText }}>
                    {nearbySearch.location.CompanyName} {nearbySearch.location.DivisionName || ''}
                  </Typography>

                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1.5 }}>
                    {RADIUS_OPTIONS_KM.map((radiusKm) => (
                      <Chip
                        key={radiusKm}
                        label={`${radiusKm} km`}
                        color={nearbySearch.radiusKm === radiusKm ? 'primary' : 'default'}
                        variant={nearbySearch.radiusKm === radiusKm ? 'filled' : 'outlined'}
                        onClick={() => setNearbySearch((current) => current ? { ...current, radiusKm } : current)}
                        sx={{
                          color: nearbySearch.radiusKm === radiusKm ? '#fff' : panelActionText,
                          borderColor: theme === 'dark' ? 'rgba(248, 251, 255, 0.28)' : undefined,
                        }}
                      />
                    ))}
                    <Chip
                      label="Clear Nearby"
                      color="error"
                      variant="filled"
                      onClick={() => setNearbySearch(null)}
                      sx={{
                        fontWeight: 700,
                        color: '#fff',
                      }}
                    />
                  </Box>

                  <Typography variant="body2" sx={{ mb: 1.25, color: panelActionText }}>
                    <strong>{nearbyLocations.length}</strong> other location{nearbyLocations.length === 1 ? '' : 's'} within {nearbySearch.radiusKm} km
                  </Typography>

                  {nearbyLocations.length ? (
                    nearbyLocations.map((location) => (
                      <Box
                        key={`nearby-${location.LocationID}`}
                        sx={{
                          py: 1,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          gap: 1.25,
                          borderTop: `1px solid ${nearbyPanelBorder}`,
                          borderRadius: '10px',
                          px: 0.5,
                          backgroundColor: Number(hoveredNearbyLocationId) === Number(location.LocationID)
                            ? (theme === 'dark' ? 'rgba(249, 115, 22, 0.18)' : 'rgba(249, 115, 22, 0.1)')
                            : 'transparent',
                        }}
                        onMouseEnter={() => handleNearbyLocationHover(location.LocationID)}
                        onMouseLeave={clearNearbyLocationHover}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
                          <Box
                            component="img"
                            src={(icons[location.SupportStatus] || defaultMarkerIcon).url}
                            alt={`${location.CompanyName || 'Location'} marker`}
                            sx={{
                              width: 18,
                              height: 26,
                              objectFit: 'contain',
                              flexShrink: 0,
                            }}
                          />
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, color: panelActionText }}>
                              {location.CompanyName} {location.DivisionName}
                            </Typography>
                            <Typography variant="caption" sx={{ display: 'block', color: panelSubtleText }}>
                              {location.distanceKm.toFixed(1)} km away
                            </Typography>
                          </Box>
                        </Box>
                        {tripStops.findIndex((stop) => getTripStopKey(stop) === getTripStopKey(location)) >= 0 ? (
                          <Chip
                            size="small"
                            color="primary"
                            label={`Stop ${tripStops.findIndex((stop) => getTripStopKey(stop) === getTripStopKey(location)) + 1}`}
                          />
                        ) : (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => addTripStop(location)}
                            sx={{
                              minHeight: 32,
                              whiteSpace: 'nowrap',
                              color: panelActionText,
                              borderColor: theme === 'dark' ? 'rgba(248, 251, 255, 0.28)' : undefined,
                            }}
                          >
                            Add Stop
                          </Button>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body2" sx={{ color: panelSubtleText }}>No other locations found in this radius.</Typography>
                  )}

                </Box>
              ) : null}

              {nearbySearch ? (
                <Box
                  sx={{
                    mb: 1.75,
                    p: 1.5,
                    borderRadius: '14px',
                    backgroundColor: nearbyAirportsSectionBackground,
                    border: `1px solid ${nearbyPanelBorder}`,
                  }}
                >
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 1,
                      mb: 1,
                      flexWrap: 'wrap',
                    }}
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        px: 1.1,
                        py: 0.45,
                        borderRadius: '999px',
                        backgroundColor: sectionTitleBackground,
                        color: sectionTitleColor,
                        boxShadow: '0 6px 18px rgba(15, 23, 42, 0.12)',
                      }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                        Nearby Airports
                      </Typography>
                    </Box>
                    <Chip
                      size="small"
                      label={`${nearestAirports.length} shown`}
                      variant="outlined"
                      sx={{
                        color: panelActionText,
                        borderColor: theme === 'dark' ? 'rgba(248, 251, 255, 0.28)' : undefined,
                      }}
                    />
                  </Box>

                  {nearbyAirportsLoading ? (
                    <Typography variant="body2" sx={{ color: panelSubtleText, mb: 1.25 }}>
                      Loading nearby airports...
                    </Typography>
                  ) : null}

                  {!nearbyAirportsLoading && nearbyAirportsError ? (
                    <Alert severity="error" sx={{ mb: 1.25 }}>
                      {nearbyAirportsError}
                    </Alert>
                  ) : null}

                  {!nearbyAirportsLoading && !nearbyAirportsError && nearestAirports.length ? (
                    nearestAirports.map((airport) => (
                      <Box
                        key={`airport-${airport.ident}-${airport.latitude}-${airport.longitude}`}
                        sx={{
                          py: 1,
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1.25,
                          borderTop: `1px solid ${nearbyPanelBorder}`,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                            {airport.iataCode || airport.ident ? (
                              <Chip
                                size="small"
                                color="primary"
                                label={airport.iataCode || airport.ident}
                                sx={{ color: '#fff', fontWeight: 700 }}
                              />
                            ) : null}
                            <Typography variant="body2" sx={{ fontWeight: 600, color: panelActionText }}>
                              {airport.name} ({airport.type === 'large_airport' ? 'Large' : 'Medium'})
                            </Typography>
                          </Box>
                          <Typography variant="caption" sx={{ display: 'block', color: panelSubtleText }}>
                            {[airport.municipality, airport.country].filter(Boolean).join(', ') || 'Location not available'}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', color: panelSubtleText }}>
                            {airport.distanceKm.toFixed(1)} km away
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'flex-end', gap: 0.75 }}>
                          {tripStops.findIndex((stop) => getTripStopKey(stop) === getTripStopKey(airport)) >= 0 ? (
                            <Chip
                              size="small"
                              color="primary"
                              label={`Stop ${tripStops.findIndex((stop) => getTripStopKey(stop) === getTripStopKey(airport)) + 1}`}
                            />
                          ) : (
                            <Button
                              size="small"
                              variant="outlined"
                              onClick={() => addTripStop(airport)}
                              sx={{
                                minHeight: 32,
                                whiteSpace: 'nowrap',
                                color: panelActionText,
                                borderColor: theme === 'dark' ? 'rgba(248, 251, 255, 0.28)' : undefined,
                              }}
                            >
                              Add Stop
                            </Button>
                          )}
                        </Box>
                      </Box>
                    ))
                  ) : null}

                  {!nearbyAirportsLoading && !nearbyAirportsError && !nearestAirports.length ? (
                    <Typography variant="body2" sx={{ color: panelSubtleText }}>
                      No medium or large airports found in this radius.
                    </Typography>
                  ) : null}
                </Box>
              ) : null}

              <Box
                sx={{
                  p: 1.5,
                  borderRadius: '14px',
                  backgroundColor: tripPlannerSectionBackground,
                  border: `1px solid ${nearbyPanelBorder}`,
                }}
              >
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                <Box>
                  <Box
                    sx={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      px: 1.1,
                      py: 0.45,
                      mb: 0.5,
                      borderRadius: '999px',
                      backgroundColor: sectionTitleBackground,
                      color: sectionTitleColor,
                      boxShadow: '0 6px 18px rgba(15, 23, 42, 0.12)',
                    }}
                  >
                    <Typography variant="subtitle2" sx={{ fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                      Trip Planner
                    </Typography>
                  </Box>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    startIcon={<FiFileText />}
                    onClick={() => setTripReportOpen(true)}
                    disabled={!tripStops.length}
                    sx={{
                      color: '#fff',
                      fontWeight: 700,
                    }}
                  >
                    Report
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="error"
                    onClick={clearTripStops}
                    disabled={!tripStops.length}
                    sx={{ fontWeight: 700 }}
                  >
                    Clear Trip
                  </Button>
                </Box>
              </Box>

              <Box sx={{ mb: 1.5 }}>
                <Typography variant="caption" sx={{ display: 'block', mb: 0.35, color: panelSubtleText }}>
                  Trip Name
                </Typography>
                <TextField
                  variant="standard"
                  fullWidth
                  value={tripName}
                  onChange={(event) => setTripName(event.target.value)}
                  placeholder="My Trip"
                  InputProps={{
                    disableUnderline: true,
                  }}
                  sx={{
                    '& .MuiInputBase-input': {
                      p: 0,
                      fontSize: '1rem',
                      fontWeight: 400,
                      lineHeight: 1.35,
                      letterSpacing: '0',
                      color: panelActionText,
                    },
                    '& .MuiInputBase-input::placeholder': {
                      color: panelSubtleText,
                      opacity: 1,
                    },
                    '& .MuiInput-underline:before': {
                      borderBottomColor: theme === 'dark' ? 'rgba(248, 251, 255, 0.22)' : 'rgba(15, 23, 42, 0.18)',
                    },
                    '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                      borderBottomColor: theme === 'dark' ? 'rgba(248, 251, 255, 0.38)' : 'rgba(15, 23, 42, 0.28)',
                    },
                    '& .MuiInput-underline:after': {
                      borderBottomColor: theme === 'dark' ? '#f8fbff' : '#111827',
                    },
                  }}
                />
              </Box>

              {tripOrigin ? (
                <Box sx={{ mb: 1.25 }}>
                  <Typography variant="caption" sx={{ display: 'block', color: panelSubtleText }}>
                    Start Location
                  </Typography>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: panelActionText }}>
                    {getLocationName(tripOrigin) || `${tripOrigin.CompanyName} ${tripOrigin.DivisionName || ''}`.trim()}
                  </Typography>
                  <Typography variant="caption" sx={{ display: 'block', color: panelSubtleText, mb: 1 }}>
                    {formatAddress(tripOrigin) || `${formatCoordinate(tripOrigin.Latitude)}, ${formatCoordinate(tripOrigin.Longitude)}`}
                  </Typography>
                  <Chip
                    size="small"
                    color="warning"
                    variant={returnToOrigin ? 'filled' : 'outlined'}
                    label="Return To Start"
                    onClick={() => setReturnToOrigin((current) => !current)}
                    sx={{
                      fontWeight: 700,
                      color: returnToOrigin ? '#111827' : '#facc15',
                      backgroundColor: returnToOrigin ? '#facc15' : 'transparent',
                      borderColor: '#facc15',
                    }}
                  />
                </Box>
              ) : null}

              {tripStops.length ? (
                tripStops.map((stop, index) => (
                  <Box
                    key={`trip-stop-${getTripStopKey(stop)}`}
                    sx={{
                      py: 1,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      borderTop: `1px solid ${nearbyPanelBorder}`,
                    }}
                  >
                    <Chip size="small" color="warning" label={`Stop ${index + 1}`} />
                    <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: panelActionText }}>
                        {getLocationName(stop)}
                      </Typography>
                      <Typography variant="caption" sx={{ display: 'block', color: panelSubtleText }}>
                        {formatAddress(stop) || 'Address not available'}
                      </Typography>
                    </Box>
                    <IconButton size="small" onClick={() => moveTripStop(index, -1)} disabled={index === 0} sx={{ color: panelActionText }}>
                      <FiArrowUp size={16} />
                    </IconButton>
                    <IconButton size="small" onClick={() => moveTripStop(index, 1)} disabled={index === tripStops.length - 1} sx={{ color: panelActionText }}>
                      <FiArrowDown size={16} />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => removeTripStop(getTripStopKey(stop))}>
                      <FiTrash2 size={16} />
                    </IconButton>
                  </Box>
                ))
              ) : (
                <Typography variant="body2" sx={{ color: panelSubtleText }}>
                  Add locations from the nearby list to build a trip route.
                </Typography>
              )}
              </Box>
            </Box>
          ) : null}
        </div>
      </div>

      <Dialog
        open={detailsDialogOpen}
        onClose={handleCloseDetailsDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '18px',
            backgroundColor: theme === 'dark' ? '#101924' : '#fffdf8',
            color: theme === 'dark' ? '#f8fbff' : '#1f2937',
          }
        }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {selectedLocationDetails?.Logo && resolveLogoSrc(selectedLocationDetails.Logo) ? (
              <Box
                component="img"
                src={resolveLogoSrc(selectedLocationDetails.Logo)}
                alt={selectedLocationDetails.CompanyName || 'Company logo'}
                sx={{
                  width: 56,
                  height: 56,
                  objectFit: 'contain',
                  borderRadius: '12px',
                  backgroundColor: '#ffffff',
                  p: 0.75,
                  boxShadow: '0 4px 12px rgba(15, 23, 42, 0.12)',
                }}
              />
            ) : null}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {selectedLocationDetails?.CompanyName || 'Location Details'}
              </Typography>
              {selectedLocationDetails?.DivisionName ? (
                <Typography variant="body2" sx={{ color: panelSubtleText }}>
                  {selectedLocationDetails.DivisionName}
                </Typography>
              ) : null}
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {detailsLoading ? (
            <Typography variant="body2" sx={{ color: panelActionText }}>Loading location summary...</Typography>
          ) : null}

          {!detailsLoading && detailsError ? (
            <Alert severity="error">{detailsError}</Alert>
          ) : null}

          {!detailsLoading && !detailsError && selectedLocationDetails ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Summary
                </Typography>
                <Typography variant="body2" sx={{ color: panelActionText }}><strong>Address:</strong> {formatAddress(selectedLocationDetails) || 'Not available'}</Typography>
                <Typography variant="body2" sx={{ color: panelActionText }}><strong>Region:</strong> {selectedLocationDetails.Region || 'Not available'}</Typography>
                <Typography variant="body2" sx={{ color: panelActionText }}><strong>Site Status:</strong> {selectedLocationDetails.SiteStatus || 'Not available'}</Typography>
                <Typography variant="body2" sx={{ color: panelActionText }}><strong>Operation Type:</strong> {selectedLocationDetails.OperationTypes?.length ? selectedLocationDetails.OperationTypes.join(', ') : 'Not available'}</Typography>
                <Typography variant="body2" sx={{ color: panelActionText }}><strong>Species:</strong> {selectedLocationDetails.Species?.length ? selectedLocationDetails.Species.join(', ') : 'Not available'}</Typography>
                <Typography variant="body2" sx={{ color: panelActionText }}><strong>SiCam Customer:</strong> {normalizeCustomerValue(selectedLocationDetails.SiCamCustomer) === '1' ? 'Yes' : normalizeCustomerValue(selectedLocationDetails.SiCamCustomer) === '0' ? 'No' : 'Not available'}</Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Nearby Locations
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {RADIUS_OPTIONS_KM.map((radiusKm) => (
                    <Button
                      key={radiusKm}
                      size="small"
                      variant="outlined"
                      onClick={() => openNearbySearch(selectedLocationDetails, radiusKm)}
                      sx={{
                        color: panelActionText,
                        borderColor: theme === 'dark' ? 'rgba(248, 251, 255, 0.28)' : undefined,
                      }}
                    >
                      {radiusKm} km
                    </Button>
                  ))}
                </Box>
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Contacts
                </Typography>
                {selectedLocationDetails.Contacts?.length ? (
                  selectedLocationDetails.Contacts.map((contact) => (
                    <Box key={contact.ContactID} sx={{ mb: 1.25 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {formatContactName(contact) || 'Unnamed Contact'}
                        {contact.Title ? ` - ${contact.Title}` : ''}
                      </Typography>
                      {contact.Email ? <Typography variant="body2" sx={{ color: panelActionText }}>{contact.Email}</Typography> : null}
                      {contact.CellPhone ? <Typography variant="body2" sx={{ color: panelActionText }}>Cell: {contact.CellPhone}</Typography> : null}
                      {contact.OfficePhone ? (
                        <Typography variant="body2" sx={{ color: panelActionText }}>
                          Office: {contact.OfficePhone}{contact.PhoneExtension ? ` ext. ${contact.PhoneExtension}` : ''}
                        </Typography>
                      ) : null}
                    </Box>
                  ))
                ) : (
                  <Typography variant="body2" sx={{ color: panelSubtleText }}>No contacts are associated with this site.</Typography>
                )}
              </Box>

              <Divider />

              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.75 }}>
                  Notes
                </Typography>
                <Typography variant="body2" sx={{ color: panelActionText }}>
                  {selectedLocationDetails.Notes ? selectedLocationDetails.Notes : 'No notes available for this site.'}
                </Typography>
              </Box>
            </Box>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={tripReportOpen}
        onClose={() => setTripReportOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '18px',
            backgroundColor: theme === 'dark' ? '#101924' : '#fffdf8',
            color: theme === 'dark' ? '#f8fbff' : '#1f2937',
          }
        }}
      >
      <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: panelActionText }}>
            <FiMapPin />
            <Box>
              <Typography variant="overline" sx={{ display: 'block', color: panelSubtleText, letterSpacing: '0.08em' }}>
                Trip Report
              </Typography>
              <Typography variant="h5" sx={{ fontWeight: 800, lineHeight: 1.1, wordBreak: 'break-word' }}>
                {getDisplayTripName(tripName)}
              </Typography>
              <Typography variant="body2" sx={{ color: panelSubtleText }}>
                {tripStops.length} stop{tripStops.length === 1 ? '' : 's'} selected, {totalTripDistanceKm.toFixed(1)} km total between stops
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent dividers>
          {tripReportMessage ? (
            <Alert
              severity={tripReportMessage.toLowerCase().includes('unable') || tripReportMessage.toLowerCase().includes('failed') ? 'error' : 'success'}
              sx={{ mb: 2 }}
            >
              {tripReportMessage}
            </Alert>
          ) : null}

          {tripReportRows.length ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {tripOrigin ? (
                <Box key={`trip-report-start-${getTripStopKey(tripOrigin)}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Chip
                      size="small"
                      color="default"
                      label="Start"
                      sx={{
                        color: theme === 'dark' ? '#f8fbff' : '#111827',
                        backgroundColor: theme === 'dark' ? 'rgba(148, 163, 184, 0.22)' : 'rgba(226, 232, 240, 0.9)',
                      }}
                    />
                    <Box
                      component="img"
                      src={(icons[tripOrigin.SupportStatus] || defaultMarkerIcon).url}
                      alt={`${getLocationName(tripOrigin) || 'Start location'} marker`}
                      sx={{ width: 18, height: 26, objectFit: 'contain' }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: panelActionText }}>
                      {getLocationName(tripOrigin) || 'Selected Start Location'}
                    </Typography>
                  </Box>
                  {tripOrigin.AirportType ? (
                    <>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Name:</strong> {tripOrigin.CompanyName || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Code:</strong> {tripOrigin.IataCode || tripOrigin.Ident || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Municipality:</strong> {tripOrigin.City || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Region:</strong> {tripOrigin.Region || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Country:</strong> {tripOrigin.Country || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Airport Type:</strong> {tripOrigin.AirportType === 'large_airport' ? 'Large' : tripOrigin.AirportType === 'medium_airport' ? 'Medium' : tripOrigin.AirportType}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Distance From Selected Location:</strong> Starting point</Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Address:</strong> {formatAddress(tripOrigin) || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Region:</strong> {tripOrigin.Region || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Site Status:</strong> {tripOrigin.SiteStatus || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Support:</strong> {supportStatusDescriptions[tripOrigin.SupportStatus] || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Coordinates:</strong> {formatCoordinate(tripOrigin.Latitude)}, {formatCoordinate(tripOrigin.Longitude)}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Distance From Previous Stop:</strong> Starting point</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Notes:</strong> {tripOrigin.Notes || 'No notes available for this location.'}</Typography>
                    </>
                  )}
                  <Divider sx={{ mt: 1.5 }} />
                </Box>
              ) : null}
              {tripReportRows.map((stop) => (
                <Box key={`trip-report-${getTripStopKey(stop)}`}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
                    <Chip
                      size="small"
                      color={stop.rowType === 'return' ? 'default' : 'warning'}
                      label={stop.rowType === 'return' ? 'Return' : `Stop ${stop.stopNumber}`}
                      sx={stop.rowType === 'return' ? {
                        color: theme === 'dark' ? '#f8fbff' : '#111827',
                        backgroundColor: theme === 'dark' ? 'rgba(148, 163, 184, 0.22)' : 'rgba(226, 232, 240, 0.9)',
                      } : undefined}
                    />
                    <Box
                      component="img"
                      src={(icons[stop.SupportStatus] || defaultMarkerIcon).url}
                      alt={`${getLocationName(stop) || 'Location'} marker`}
                      sx={{ width: 18, height: 26, objectFit: 'contain' }}
                    />
                    <Typography variant="subtitle1" sx={{ fontWeight: 700, color: panelActionText }}>
                      {getLocationName(stop)}
                    </Typography>
                  </Box>
                  {stop.AirportType ? (
                    <>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Name:</strong> {stop.CompanyName || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Code:</strong> {stop.IataCode || stop.Ident || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Municipality:</strong> {stop.City || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Region:</strong> {stop.Region || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Country:</strong> {stop.Country || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Airport Type:</strong> {stop.AirportType === 'large_airport' ? 'Large' : stop.AirportType === 'medium_airport' ? 'Medium' : stop.AirportType}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Distance From Selected Location:</strong> {stop.DistanceFromSelectedKm ? `${Number(stop.DistanceFromSelectedKm).toFixed(1)} km` : 'Not available'}</Typography>
                    </>
                  ) : (
                    <>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Address:</strong> {formatAddress(stop) || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Region:</strong> {stop.Region || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Site Status:</strong> {stop.SiteStatus || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Support:</strong> {supportStatusDescriptions[stop.SupportStatus] || 'Not available'}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Coordinates:</strong> {formatCoordinate(stop.Latitude)}, {formatCoordinate(stop.Longitude)}</Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}>
                        <strong>Distance From Previous Stop:</strong> {stop.segmentDistanceKm ? `${stop.segmentDistanceKm.toFixed(1)} km` : 'Starting point'}
                      </Typography>
                      <Typography variant="body2" sx={{ color: panelActionText }}><strong>Notes:</strong> {stop.Notes || 'No notes available for this location.'}</Typography>
                    </>
                  )}
                  {stop.stopNumber < tripReportRows.length ? <Divider sx={{ mt: 1.5 }} /> : null}
                </Box>
              ))}
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: panelSubtleText }}>No trip stops selected yet.</Typography>
          )}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={emailTripReport} disabled={!tripReportRows.length || sendingTripReport} sx={{ color: panelActionText }}>
            {sendingTripReport ? 'Sending...' : 'Email This Report To Me'}
          </Button>
          <Button onClick={() => setTripReportOpen(false)} sx={{ color: panelActionText }}>Close</Button>
        </DialogActions>
      </Dialog>

    </div>
  );
};

export default Maps;
